import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from './useAuth.js';
import { useInvite, useAcceptInvite } from '../team/hooks/useTeam.js';

const ROLE_LABEL: Record<string, string> = {
  manager:    'Manager',
  programmer: 'Programmer',
  operator:   'Operator',
};

function readSearchParams(): { token: string | null; shop: string | null; state: string | null } {
  const p = new URLSearchParams(window.location.search);
  return { token: p.get('token'), shop: p.get('shop'), state: p.get('state') };
}

export function AcceptInvitePage(): JSX.Element {
  const { token, shop: shopId, state: urlState } = readSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, shopId: userShopId } = useAuth();

  const inviteQuery = useInvite(shopId, token);
  const acceptMutation = useAcceptInvite(shopId, token);

  const [accepted, setAccepted] = useState(false);

  const invite = inviteQuery.data;

  useEffect(() => {
    if (urlState === 'already_accepted' || urlState === 'expired') {
      // URL state already set by the redirect — just render info
    }
  }, [urlState]);

  const handleAccept = async () => {
    try {
      await acceptMutation.mutateAsync();
      setAccepted(true);
      setTimeout(() => void navigate({ to: '/dashboard' }), 2000);
    } catch {
      // Error shown in UI
    }
  };

  if (!token || !shopId) {
    return <InvitePage><ErrorMessage>Invalid invitation link. The link may be incomplete.</ErrorMessage></InvitePage>;
  }

  if (authLoading || inviteQuery.isLoading) {
    return (
      <InvitePage>
        <div className="space-y-2">
          {[0, 1].map(i => (
            <div key={i} className="h-6 rounded bg-card/50 animate-pulse" />
          ))}
        </div>
      </InvitePage>
    );
  }

  if (inviteQuery.isError || !invite) {
    return (
      <InvitePage>
        <ErrorMessage>Invitation not found. It may have expired or already been used.</ErrorMessage>
        <GoToLogin />
      </InvitePage>
    );
  }

  if (invite.status === 'expired' || urlState === 'expired') {
    return (
      <InvitePage>
        <ErrorMessage>This invitation has expired. Ask a manager to send a new one.</ErrorMessage>
        <GoToLogin />
      </InvitePage>
    );
  }

  if (invite.status === 'accepted' || urlState === 'already_accepted') {
    return (
      <InvitePage>
        <SuccessBox>This invitation has already been accepted.</SuccessBox>
        {isAuthenticated
          ? <NavButton label="Go to dashboard" onClick={() => void navigate({ to: '/dashboard' })} />
          : <GoToLogin />}
      </InvitePage>
    );
  }

  if (accepted) {
    return (
      <InvitePage>
        <SuccessBox>Invitation accepted! Redirecting to your dashboard…</SuccessBox>
      </InvitePage>
    );
  }

  const wrongShop = isAuthenticated && userShopId && userShopId !== shopId;

  return (
    <InvitePage>
      <div className="space-y-4">
        <div className="rounded-xl border border-border bg-surface/50 px-5 py-4 space-y-2">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted">Invitation for</p>
          <p className="text-sm font-mono text-text">{invite.email}</p>
          <p className="text-xs font-mono text-text-dim">
            Shop: <span className="text-text">{invite.shop_id}</span>
          </p>
          <p className="text-xs font-mono text-text-dim">
            Role: <span className="text-text capitalize">{ROLE_LABEL[invite.role] ?? invite.role}</span>
          </p>
          <p className="text-[10px] font-mono text-muted">
            Expires: {new Date(invite.expires_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>

        {wrongShop && (
          <p className="text-xs font-mono text-yellow px-1">
            You are signed in to a different shop. Sign out and sign in with{' '}
            <strong>{invite.email}</strong> to accept this invitation.
          </p>
        )}

        {acceptMutation.isError && (
          <p className="text-xs font-mono text-red px-1">
            {acceptMutation.error instanceof Error ? acceptMutation.error.message : 'Failed to accept invitation.'}
          </p>
        )}

        {isAuthenticated && !wrongShop ? (
          <button
            type="button"
            onClick={() => void handleAccept()}
            disabled={acceptMutation.isPending}
            className="w-full py-2.5 rounded-md bg-accent text-[#001821] font-mono text-sm font-semibold hover:brightness-110 transition-all disabled:opacity-50"
          >
            {acceptMutation.isPending ? 'Accepting…' : 'Accept Invitation'}
          </button>
        ) : (
          <div className="space-y-2">
            <p className="text-xs font-mono text-text-dim text-center">
              Sign in to accept this invitation.
            </p>
            <GoToLogin />
          </div>
        )}
      </div>
    </InvitePage>
  );
}

// ── Layout helpers ────────────────────────────────────────────────────────────

function InvitePage({ children }: { readonly children: React.ReactNode }): JSX.Element {
  return (
    <div
      className="min-h-dvh flex items-center justify-center p-6"
      style={{ backgroundColor: 'var(--color-bg)' }}
    >
      <div className="w-full max-w-sm space-y-6">
        <div>
          <p className="font-mono text-base font-semibold">
            <span style={{ color: 'var(--color-accent)' }}>werk</span>
            <span style={{ color: 'var(--color-muted)' }}>OS</span>
          </p>
          <h1 className="mt-2 font-mono text-sm text-text-dim">Team invitation</h1>
        </div>
        {children}
      </div>
    </div>
  );
}

function ErrorMessage({ children }: { readonly children: React.ReactNode }): JSX.Element {
  return (
    <div className="rounded-xl border border-red/30 bg-red/5 px-4 py-3">
      <p className="text-sm font-mono text-red">{children}</p>
    </div>
  );
}

function SuccessBox({ children }: { readonly children: React.ReactNode }): JSX.Element {
  return (
    <div className="rounded-xl border border-green/30 bg-green/5 px-4 py-3">
      <p className="text-sm font-mono text-green">{children}</p>
    </div>
  );
}

function GoToLogin(): JSX.Element {
  return (
    <a
      href="/login"
      className="block w-full text-center py-2.5 rounded-md border border-border text-sm font-mono text-text-dim hover:text-text hover:border-accent transition-colors"
    >
      Sign in
    </a>
  );
}

function NavButton({ label, onClick }: { readonly label: string; readonly onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-center py-2.5 rounded-md border border-border text-sm font-mono text-text-dim hover:text-text hover:border-accent transition-colors"
    >
      {label}
    </button>
  );
}
